import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Loader2, Users, Search, Trash2, Shield, ShieldCheck, 
  Mail, Calendar, Gift, UserX, ChevronDown, ChevronUp,
  CreditCard, DollarSign, CheckCircle, XCircle, Crown, Activity
} from "lucide-react";

const UsersManager = () => {
  const { authAxios, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await authAxios.get('/admin/users');
      setUsers(response.data);
    } catch (e) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) return;
    
    try {
      await authAxios.put(`/admin/users/${userId}/role?role=${newRole}`);
      toast.success(`User role updated to ${newRole}`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) return;
    
    try {
      await authAxios.delete(`/admin/users/${userId}`);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Filter users based on search and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === "" || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  // Stats
  const totalUsers = users.length;
  const adminCount = users.filter(u => u.role === 'admin').length;
  const userCount = users.filter(u => u.role === 'user').length;
  const referredCount = users.filter(u => u.referred_by).length;
  const paidUsers = users.filter(u => u.subscription_status === 'active').length;
  const totalRevenue = users.reduce((sum, u) => sum + (u.total_paid || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-neon-blue" />
          Registered Users ({totalUsers})
        </h2>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="cyber-card p-4">
          <div className="flex items-center gap-2 text-neon-blue">
            <Users className="w-4 h-4" />
            <span className="font-orbitron text-xs">TOTAL</span>
          </div>
          <p className="font-orbitron text-2xl text-white mt-1">{totalUsers}</p>
        </div>
        <div className="cyber-card p-4">
          <div className="flex items-center gap-2 text-neon-pink">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-orbitron text-xs">ADMINS</span>
          </div>
          <p className="font-orbitron text-2xl text-white mt-1">{adminCount}</p>
        </div>
        <div className="cyber-card p-4">
          <div className="flex items-center gap-2 text-neon-green">
            <Users className="w-4 h-4" />
            <span className="font-orbitron text-xs">MEMBERS</span>
          </div>
          <p className="font-orbitron text-2xl text-white mt-1">{userCount}</p>
        </div>
        <div className="cyber-card p-4">
          <div className="flex items-center gap-2 text-yellow-500">
            <Gift className="w-4 h-4" />
            <span className="font-orbitron text-xs">REFERRED</span>
          </div>
          <p className="font-orbitron text-2xl text-white mt-1">{referredCount}</p>
        </div>
        <div className="cyber-card p-4 border-green-500/30">
          <div className="flex items-center gap-2 text-green-400">
            <Crown className="w-4 h-4" />
            <span className="font-orbitron text-xs">PAID SUBS</span>
          </div>
          <p className="font-orbitron text-2xl text-white mt-1">{paidUsers}</p>
        </div>
        <div className="cyber-card p-4 border-green-500/30">
          <div className="flex items-center gap-2 text-green-400">
            <DollarSign className="w-4 h-4" />
            <span className="font-orbitron text-xs">REVENUE</span>
          </div>
          <p className="font-orbitron text-2xl text-white mt-1">${totalRevenue.toFixed(0)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-void-black border-white/10 pl-10"
            data-testid="users-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="bg-void-black border-white/10 w-full sm:w-40" data-testid="users-role-filter">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent className="bg-void-black border-white/10">
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
            <SelectItem value="user">Members</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.map(user => {
          const isCurrentUser = user.id === currentUser?.id;
          const isExpanded = expandedUser === user.id;
          
          return (
            <div 
              key={user.id} 
              className={`cyber-card overflow-hidden ${isCurrentUser ? 'ring-1 ring-neon-blue/50' : ''}`}
              data-testid={`user-card-${user.id}`}
            >
              {/* User Row */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedUser(isExpanded ? null : user.id)}
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    user.role === 'admin' ? 'bg-neon-pink/20' : 'bg-neon-blue/20'
                  }`}>
                    {user.role === 'admin' ? (
                      <ShieldCheck className="w-5 h-5 text-neon-pink" />
                    ) : (
                      <Users className="w-5 h-5 text-neon-blue" />
                    )}
                  </div>
                  
                  {/* Info */}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-orbitron text-sm text-white">{user.name}</h3>
                      {isCurrentUser && (
                        <span className="px-2 py-0.5 bg-neon-blue/20 text-neon-blue text-[10px] font-orbitron rounded">YOU</span>
                      )}
                      {user.role === 'admin' && (
                        <span className="px-2 py-0.5 bg-neon-pink/20 text-neon-pink text-[10px] font-orbitron rounded">ADMIN</span>
                      )}
                    </div>
                    <p className="font-rajdhani text-xs text-gray-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {user.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="hidden md:flex items-center gap-3">
                    {/* Subscription Status Badge */}
                    {user.subscription_status === 'active' ? (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 border border-green-500/30 rounded">
                        <Crown className="w-3 h-3 text-green-400" />
                        <span className="font-orbitron text-[10px] text-green-400">PAID</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 px-2 py-1 bg-gray-500/20 border border-gray-500/30 rounded">
                        <XCircle className="w-3 h-3 text-gray-500" />
                        <span className="font-orbitron text-[10px] text-gray-500">FREE</span>
                      </div>
                    )}
                    {/* Total Paid */}
                    {user.total_paid > 0 && (
                      <div className="text-right">
                        <p className="font-orbitron text-xs text-green-400">${user.total_paid.toFixed(0)}</p>
                        <p className="font-rajdhani text-[10px] text-gray-500">spent</p>
                      </div>
                    )}
                  </div>
                  <div className="hidden md:block text-right">
                    <p className="font-rajdhani text-xs text-gray-500">Joined</p>
                    <p className="font-orbitron text-xs text-gray-400">{formatDate(user.created_at)}</p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-white/10 p-4 bg-white/5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="font-orbitron text-[10px] text-gray-500">USER ID</p>
                      <p className="font-rajdhani text-xs text-gray-400 break-all">{user.id}</p>
                    </div>
                    <div>
                      <p className="font-orbitron text-[10px] text-gray-500">REFERRAL CODE</p>
                      <p className="font-orbitron text-xs text-neon-green">{user.referral_code || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-orbitron text-[10px] text-gray-500">REFERRED BY</p>
                      <p className="font-rajdhani text-xs text-gray-400">{user.referred_by || 'Direct signup'}</p>
                    </div>
                    <div>
                      <p className="font-orbitron text-[10px] text-gray-500">REFERRAL CREDITS</p>
                      <p className="font-orbitron text-xs text-yellow-500">${user.referral_credits?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="font-orbitron text-[10px] text-gray-500">JOINED</p>
                      <p className="font-rajdhani text-xs text-gray-400">{formatDate(user.created_at)}</p>
                    </div>
                    <div>
                      <p className="font-orbitron text-[10px] text-gray-500">SUBSCRIPTION STATUS</p>
                      <div className="flex items-center gap-2 mt-1">
                        {user.subscription_status === 'active' ? (
                          <span className="flex items-center gap-1 text-green-400">
                            <CheckCircle className="w-3 h-3" />
                            <span className="font-orbitron text-xs">ACTIVE</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-500">
                            <XCircle className="w-3 h-3" />
                            <span className="font-orbitron text-xs">NO SUBSCRIPTION</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Payment/Subscription Info */}
                  <div className="border-t border-white/10 pt-4 mb-4">
                    <h4 className="font-orbitron text-xs text-neon-blue flex items-center gap-2 mb-3">
                      <CreditCard className="w-4 h-4" />
                      PAYMENT & SUBSCRIPTION INFO
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-black/30 p-3 rounded">
                        <p className="font-orbitron text-[10px] text-gray-500">CURRENT PLAN</p>
                        <p className="font-rajdhani text-sm text-white">{user.current_plan || 'None'}</p>
                      </div>
                      <div className="bg-black/30 p-3 rounded">
                        <p className="font-orbitron text-[10px] text-gray-500">TOTAL SPENT</p>
                        <p className="font-orbitron text-sm text-green-400">${(user.total_paid || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-black/30 p-3 rounded">
                        <p className="font-orbitron text-[10px] text-gray-500">PAYMENTS</p>
                        <p className="font-orbitron text-sm text-white">{user.payment_count || 0}</p>
                      </div>
                      <div className="bg-black/30 p-3 rounded">
                        <p className="font-orbitron text-[10px] text-gray-500">FORM ANALYZER</p>
                        <p className={`font-orbitron text-sm ${user.has_form_analyzer_access ? 'text-green-400' : 'text-gray-500'}`}>
                          {user.has_form_analyzer_access ? 'ACCESS' : 'LOCKED'}
                        </p>
                      </div>
                    </div>

                    {/* Payment History */}
                    {user.payments && user.payments.length > 0 && (
                      <div>
                        <p className="font-orbitron text-[10px] text-gray-500 mb-2">PAYMENT HISTORY</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {user.payments.map((payment, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-black/30 p-2 rounded text-xs">
                              <div className="flex items-center gap-2">
                                <DollarSign className="w-3 h-3 text-green-400" />
                                <span className="text-white">{payment.plan_name || payment.item_name || 'Payment'}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="font-orbitron text-green-400">${payment.amount?.toFixed(2)}</span>
                                <span className="text-gray-500">{formatDate(payment.created_at)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {!isCurrentUser && (
                    <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                      {user.role !== 'admin' && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRoleChange(user.id, 'admin'); }}
                            className="flex items-center gap-2 px-3 py-2 bg-neon-pink/10 border border-neon-pink/30 text-neon-pink text-xs font-orbitron hover:bg-neon-pink/20 transition-colors"
                            data-testid={`make-admin-${user.id}`}
                          >
                            <Shield className="w-4 h-4" />
                            MAKE ADMIN
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(user.id, user.name); }}
                            className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-500 text-xs font-orbitron hover:bg-red-500/20 transition-colors"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <UserX className="w-4 h-4" />
                            DELETE USER
                          </button>
                        </>
                      )}
                      {user.role === 'admin' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRoleChange(user.id, 'user'); }}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-500/10 border border-gray-500/30 text-gray-400 text-xs font-orbitron hover:bg-gray-500/20 transition-colors"
                          data-testid={`remove-admin-${user.id}`}
                        >
                          <Shield className="w-4 h-4" />
                          REMOVE ADMIN
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="no-users-state">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">
            {searchQuery || roleFilter !== 'all' 
              ? "No users match your filters" 
              : "No users registered yet"}
          </p>
        </div>
      )}
    </div>
  );
};

export default UsersManager;
